import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  useRouterState,
} from "@tanstack/react-router";

import { App } from "./app";
import { LoadingView } from "./components/feedback";
import { ADMIN_ROUTES } from "./constants";

/**
 * 初期表示をお問い合わせ一覧へ転送する
 *
 * @returns お問い合わせ一覧への転送
 */
const IndexRouteScreen = () => {
  return <Navigate replace to={ADMIN_ROUTES.support} />;
};

/**
 * 未定義パスを現在の管理機能の一覧へ転送する
 *
 * @returns 管理機能一覧への転送
 */
const NotFoundRouteScreen = () => {
  // 未定義パスを取得する
  const pathname = useRouterState({
    select: (state) => {
      return state.location.pathname;
    },
  });
  const fallbackPath = pathname.startsWith(ADMIN_ROUTES.push)
    ? ADMIN_ROUTES.push
    : ADMIN_ROUTES.support;

  return <Navigate replace to={fallbackPath} />;
};

/**
 * 遅延ルートの読み込み表示
 *
 * @returns 読み込み表示
 */
const RoutePendingScreen = () => {
  return <LoadingView label="画面を読み込んでいます" />;
};

/**
 * 管理画面の最上位ルート
 */
const rootRoute = createRootRoute({
  component: App,
  notFoundComponent: NotFoundRouteScreen,
});

/**
 * 管理画面の初期ルート
 */
const indexRoute = createRoute({
  component: IndexRouteScreen,
  getParentRoute: () => {
    return rootRoute;
  },
  path: ADMIN_ROUTES.root,
});

/**
 * お問い合わせ一覧ルート
 */
const supportRoute = createRoute({
  getParentRoute: () => {
    return rootRoute;
  },
  path: ADMIN_ROUTES.support,
}).lazy(async () => {
  const routeModule = await import("./routes/support.lazy");
  return routeModule.Route;
});

/**
 * お問い合わせ詳細ルート
 */
const supportTicketRoute = createRoute({
  getParentRoute: () => {
    return rootRoute;
  },
  path: ADMIN_ROUTES.supportTicket,
}).lazy(async () => {
  const routeModule = await import("./routes/support-ticket.lazy");
  return routeModule.Route;
});

/**
 * お知らせ配信履歴ルート
 */
const pushRoute = createRoute({
  getParentRoute: () => {
    return rootRoute;
  },
  path: ADMIN_ROUTES.push,
}).lazy(async () => {
  const routeModule = await import("./routes/push.lazy");
  return routeModule.Route;
});

/**
 * お知らせ配信作成ルート
 */
const pushNewRoute = createRoute({
  getParentRoute: () => {
    return rootRoute;
  },
  path: ADMIN_ROUTES.pushNew,
}).lazy(async () => {
  const routeModule = await import("./routes/push-new.lazy");
  return routeModule.Route;
});

/**
 * お知らせ配信詳細ルート
 */
const pushCampaignRoute = createRoute({
  getParentRoute: () => {
    return rootRoute;
  },
  path: ADMIN_ROUTES.pushCampaign,
}).lazy(async () => {
  const routeModule = await import("./routes/push-campaign.lazy");
  return routeModule.Route;
});

/**
 * お知らせ配信編集ルート
 */
const pushCampaignEditRoute = createRoute({
  getParentRoute: () => {
    return rootRoute;
  },
  path: ADMIN_ROUTES.pushCampaignEdit,
}).lazy(async () => {
  const routeModule = await import("./routes/push-campaign-edit.lazy");
  return routeModule.Route;
});

/**
 * 管理画面のルートツリー
 */
export const routeTree = rootRoute.addChildren([
  indexRoute,
  supportRoute,
  supportTicketRoute,
  pushRoute,
  pushNewRoute,
  pushCampaignRoute,
  pushCampaignEditRoute,
]);

/**
 * 管理画面のTanStack Router
 */
export const router = createRouter({
  defaultPendingComponent: RoutePendingScreen,
  routeTree,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
